import React, { useState } from "react";
import { CheckCircleOutlined, FilterOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Popover, Segmented, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { useTeamMemberTaskContext } from "../context/TeamMemberTaskContext";
import { CategoryView } from "./CategoryView";
import { IncomingView } from "./IncomingView";

type ViewType = "calendar" | "categories";

export interface TaskListFilters {
  showCompleted: boolean;
  traineeId?: string;
}

interface TraineeInfo {
  id: string;
  firstName: string;
  lastName: string | null;
  email?: string | null;
}

interface TeamMemberTaskListProps {
  traineeId?: string;
  trainee?: TraineeInfo | null;
  title?: string;
  embedded?: boolean;
  hideCategoryView?: boolean;
}

export const TeamMemberTaskList: React.FC<TeamMemberTaskListProps> = ({
  traineeId,
  trainee,
  title,
  embedded = false,
  hideCategoryView = false,
}) => {
  const { t } = useTranslation("dashboard");
  const [view, setView] = useState<ViewType>("calendar");
  const [filters, setFilters] = useState<TaskListFilters>({ showCompleted: false, traineeId });
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { openTaskForm } = useTeamMemberTaskContext();

  const handleAddTask = () => {
    openTaskForm(traineeId ? { traineeId, trainee } : undefined);
  };

  const isTraineeView = Boolean(traineeId);

  const activeFiltersCount = filters.showCompleted ? 1 : 0;

  const filterContent = (
    <div className="flex min-w-[200px] flex-col gap-3">
      <Typography.Text strong>{t("tasks.filters")}</Typography.Text>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <CheckCircleOutlined className="text-green-500" />
          <span>{t("tasks.showCompleted")}</span>
        </div>
        <Switch
          size="small"
          checked={filters.showCompleted}
          onChange={(checked) => setFilters((f) => ({ ...f, showCompleted: checked }))}
        />
      </div>
    </div>
  );

  const headerActions = (
    <div className="flex items-center gap-2">
      <Popover
        content={filterContent}
        trigger="click"
        placement="bottomRight"
        open={filterPopoverOpen}
        onOpenChange={setFilterPopoverOpen}
      >
        <Button
          icon={<FilterOutlined />}
          type={activeFiltersCount > 0 ? "primary" : "default"}
          ghost={activeFiltersCount > 0}
        >
          {t("tasks.filters")}
          {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
        </Button>
      </Popover>
      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTask}>
        {t("tasks.addTask")}
      </Button>
    </div>
  );

  const content = (
    <>
      {!isTraineeView && !hideCategoryView && (
        <div className="mb-4">
          <Segmented
            value={view}
            onChange={(value) => setView(value as ViewType)}
            options={[
              { value: "calendar", label: t("tasks.calendar") },
              { value: "categories", label: t("tasks.categories") },
            ]}
            block
          />
        </div>
      )}

      {isTraineeView || hideCategoryView ? (
        <IncomingView filters={filters} />
      ) : view === "calendar" ? (
        <IncomingView filters={filters} />
      ) : (
        <CategoryView filters={filters} />
      )}
    </>
  );

  if (embedded) {
    return (
      <div>
        <div className="mb-4 flex justify-end">{headerActions}</div>
        {content}
      </div>
    );
  }

  return (
    <Card title={title ?? t("tasks.title")} extra={headerActions}>
      {content}
    </Card>
  );
};
